/**
 * Lightweight in-memory search index for cart products.
 *
 * For large carts the per-keystroke `searchProductUsingFields` cost becomes
 * prohibitive because every product is repeatedly walked and every field is
 * re-lowercased. The helpers below pre-compute one lowercased token string per
 * product so each subsequent search is a single `indexOf` per product.
 *
 * Used in two places:
 *  - `composables/useCartProducts.js` (V2 single-cart search)
 *  - `views/apps/checkout-v2/CartList.vue` (legacy multi-store cart search)
 */

import {
  CART_PRODUCTS_SEARCH_FIELDS,
  CART_PRODUCTS_SEARCH_ITEMS_FIELDS,
} from '@/constants'

// Below this product count the original field-by-field matcher is fast enough
// and callers can skip the index entirely to avoid the up-front build cost.
export const CART_SEARCH_INDEX_THRESHOLD = 50

function appendIndexedFieldTokens(parts, source, fields) {
  for (const field of fields) {
    const value = source?.[field]
    if (value) parts.push(String(value).toLowerCase())
  }
}

// U+0000 (NUL) is a safe per-field delimiter: it cannot appear in any
// realistic user-facing field value or query input, so `indexOf(query)`
// will never accidentally match across a field boundary. Without the
// delimiter, name="denim" + styleCode="jacket" would produce the index
// string "denimjacket" and falsely match a query like "mja". Built via
// String.fromCharCode so the literal NUL doesn't end up in source.
const INDEX_FIELD_SEPARATOR = String.fromCharCode(0)

export function buildSearchIndexEntry(product) {
  const parts = []
  appendIndexedFieldTokens(parts, product, CART_PRODUCTS_SEARCH_FIELDS)
  if (Array.isArray(product?.items)) {
    for (const item of product.items) {
      appendIndexedFieldTokens(parts, item, CART_PRODUCTS_SEARCH_ITEMS_FIELDS)
    }
  }
  return parts.join(INDEX_FIELD_SEPARATOR)
}

export function buildCartSearchIndex(productsList) {
  const index = Object.create(null)
  if (!Array.isArray(productsList)) return index
  for (const product of productsList) {
    if (product && product._id) {
      index[product._id] = buildSearchIndexEntry(product)
    }
  }
  return index
}

function normalizeSearchQuery(rawQuery) {
  return rawQuery?.trim()?.toLowerCase() || ''
}

function indexedProductMatches(index, productId, query) {
  const tokens = index[productId]
  return Boolean(tokens) && tokens.indexOf(query) !== -1
}

export function filterProductsByIndex(productsList, index, rawQuery) {
  const query = normalizeSearchQuery(rawQuery)
  if (!query) return productsList.slice()
  return productsList.filter(product => indexedProductMatches(index, product?._id, query))
}
